import React, { useState } from 'react';
import { Box, Typography, IconButton, CircularProgress, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Save, Cancel, Edit } from '@mui/icons-material';
import { useTranslate } from 'react-admin';

export const CHARGE_DECIMAL_PLACES_SETTING_KEYS: readonly string[] = ['wallet_charge_decimal_places', 'rfid_charge_decimal_places'];

type ChargeDecimalPlaces = 1 | 2;

const asPlaces = (value: unknown): ChargeDecimalPlaces | null => {
  const parsed = Number(value);
  return parsed === 1 || parsed === 2 ? parsed : null;
};

interface ChargeDecimalPlacesSelectorProps {
  currentValue: unknown;
  canEdit: boolean;
  onSave: (value: ChargeDecimalPlaces) => Promise<Record<string, string>>;
}

export const ChargeDecimalPlacesSelector: React.FC<ChargeDecimalPlacesSelectorProps> = ({ currentValue, canEdit, onSave }) => {
  const translate = useTranslate();
  const current = asPlaces(currentValue);
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState<ChargeDecimalPlaces | ''>(current === null ? '' : current);
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState('');

  const label = (places: ChargeDecimalPlaces) =>
    translate(places === 1 ? 'settings.decimalPlaces.one' : 'settings.decimalPlaces.two');

  const handleCancel = () => {
    setIsEditing(false);
    setValue(current === null ? '' : current);
    setFieldError('');
  };

  const handleSave = async () => {
    if (value === '') return;
    setSaving(true);
    try {
      const errors = await onSave(value);
      const firstError = errors.value || Object.values(errors)[0] || '';
      setFieldError(firstError);
      if (!firstError) setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Select
          size="small"
          value={value === '' ? '' : String(value)}
          onChange={(event: SelectChangeEvent) => setValue(Number(event.target.value) === 2 ? 2 : 1)}
          disabled={saving}
          sx={{ minWidth: { xs: 0, sm: 320 } }}
          inputProps={{ 'aria-label': translate('settings.decimalPlaces.choose') }}
        >
          <MenuItem value="1">{label(1)}</MenuItem>
          <MenuItem value="2">{label(2)}</MenuItem>
        </Select>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
          <IconButton onClick={handleSave} color="primary" disabled={saving || value === ''} aria-label={translate('settings.decimalPlaces.save')}>
            {saving ? <CircularProgress size={20} /> : <Save />}
          </IconButton>
          <IconButton onClick={handleCancel} disabled={saving} aria-label={translate('settings.cancel')}>
            <Cancel />
          </IconButton>
        </Box>
        {fieldError && (
          <Typography variant="caption" color="error">
            {fieldError}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Typography variant="body1" color={current === null ? 'error' : undefined}>
        {current === null ? translate('settings.decimalPlaces.invalid', { value: String(currentValue) }) : label(current)}
      </Typography>
      {canEdit && (
        <IconButton onClick={() => setIsEditing(true)} size="small" color="primary" aria-label={translate('settings.decimalPlaces.edit')}>
          <Edit fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
